<template>
  <div
    class="rp"
  >

    <!-- ================================================================
         HERO BANNER
    ================================================================ -->
    <div class="rp-hero">
      <span class="rp-hero-orb rp-hero-orb--a" />
      <span class="rp-hero-orb rp-hero-orb--b" />
      <span class="rp-hero-orb rp-hero-orb--c" />

      <div class="rp-hero-inner">
        <!-- left: icon + title -->
        <div class="d-flex align-items-center">
          <div class="rp-hero-icon-wrap">
            <feather-icon
              icon="ClipboardIcon"
              size="26"
            />
          </div>
          <div class="ml-1 ml-md-2">
            <p class="rp-hero-eyebrow mb-0">
              Management portal
            </p>
            <h1 class="rp-hero-title mb-0">
              {{ title }}
            </h1>
          </div>
        </div>

        <!-- center: stat badges -->
        <div class="rp-stat-row">
          <div class="rp-stat">
            <div class="rp-stat-icon rp-stat-icon--violet">
              <feather-icon
                icon="DatabaseIcon"
                size="14"
              />
            </div>
            <div>
              <div class="rp-stat-value">
                {{ totalRows !== null ? totalRows : '—' }}
              </div>
              <div class="rp-stat-label">
                Total requests
              </div>
            </div>
          </div>
          <div class="rp-stat">
            <div class="rp-stat-icon rp-stat-icon--cyan">
              <feather-icon
                icon="ActivityIcon"
                size="14"
              />
            </div>
            <div>
              <div class="rp-stat-value">
                Live
              </div>
              <div class="rp-stat-label">
                Real-time data
              </div>
            </div>
          </div>
          <div class="rp-stat">
            <div class="rp-stat-icon rp-stat-icon--pink">
              <feather-icon
                icon="ClockIcon"
                size="14"
              />
            </div>
            <div>
              <div class="rp-stat-value">
                Page {{ page }}
              </div>
              <div class="rp-stat-label">
                Current page
              </div>
            </div>
          </div>
        </div>

        <!-- right: actions -->
        <div class="rp-hero-actions rp-hero-actions--wrap">
          <FindRequest
            :data-load="dataLoad"
            :get-all-data-pass="getAllData"
          />

          <button
            class="rp-pill-btn ml-50"
            @click="advancedOpen = !advancedOpen"
          >
            <feather-icon
              :icon="advancedOpen ? 'ChevronUpIcon' : 'SlidersIcon'"
              size="15"
            />
            <span>{{ advancedOpen ? 'Hide' : 'Filters' }}</span>
            <span
              v-if="advancedOpen"
              class="rp-pill-dot"
            />
          </button>
          <div class="rp-export-btn">
            <MakeExeclRequest
              :data-load="dataLoad"
              :param-exel="paramExel"
              :columns="visibleColumns"
              :api-url="apiUrl"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- ================================================================
         PRIMARY FILTERS PANEL
    ================================================================ -->
    <div
      :key="`quick-${filtersKey}`"
      class="rp-filters-card"
    >
      <div class="rp-filters-header">
        <feather-icon
          icon="SearchIcon"
          size="15"
          class="mr-50"
        />
        <span>Quick filters</span>
      </div>

      <div class="rp-filters-body">
        <b-row class="align-items-start">
          <b-col
            cols="12"
            lg="5"
          >
            <div class="rp-fgroup">
              <div class="rp-fcontrol">
                <SelectAndSearch :data-load="dataLoad" />
              </div>
            </div>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            lg="3"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel rp-flabel--violet">
                <feather-icon
                  icon="ShieldIcon"
                  size="12"
                />
                Internal status
              </label>
              <div class="rp-fcontrol">
                <selectInternalStatuses
                  :select-internal-statuses="true"
                  :data-load="dataLoad"
                  :internal-statuses="internalStatuses"
                />
              </div>
            </div>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            lg="4"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel rp-flabel--cyan">
                <feather-icon
                  icon="GlobeIcon"
                  size="12"
                />
                External status
              </label>
              <div class="rp-fcontrol">
                <selectExternalStatuses
                  :select-external-statuses="true"
                  :data-load="dataLoad"
                  :external-statuses="externalStatuses"
                />
              </div>
            </div>
          </b-col>
        </b-row>

        <div class="rp-quick-footer">
          <div class="d-flex align-items-center rp-footer-filters">
            <div class="rp-type-select mr-50">
              <FilterType :data-load="dataLoad" />
            </div>

            <button
              class="rp-reset-btn mr-50"
              @click="resetFilters"
            >
              <feather-icon
                icon="RotateCcwIcon"
                size="13"
                class="mr-50"
              />
              <span>Reset</span>
            </button>
          </div>

          <button
            class="rp-search-btn"
            @click="getAllData(1)"
          >
            <feather-icon
              icon="SearchIcon"
              size="14"
              class="mr-50"
            />
            <span>Search</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ================================================================
         ADVANCED FILTERS PANEL (collapsible)
    ================================================================ -->
    <b-collapse
      :key="`adv-${filtersKey}`"
      v-model="advancedOpen"
      class="rp-advanced-collapse"
    >
      <div class="rp-advanced-card">
        <div class="rp-advanced-heading">
          <feather-icon
            icon="FilterIcon"
            size="15"
            class="mr-50"
          />
          Advanced filters
          <span class="rp-advanced-sub ml-1">Dates · Office · Source · Reviewers</span>
        </div>

        <b-row class="align-items-start">
          <b-col
            cols="12"
            md="6"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel rp-flabel--amber">
                <feather-icon
                  icon="AwardIcon"
                  size="12"
                />
                Certificate
              </label>
              <div class="rp-fcontrol">
                <selectCertificateReq
                  :certificate-options="certificate"
                  :selectcertificate="true"
                  :data-load="dataLoad"
                  @getIdCertificate="handleCertificateId"
                  @getSubCertificateIdOptions="handleSubCertificateIdOptions"
                />
              </div>
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel rp-flabel--rose">
                <feather-icon
                  icon="BookmarkIcon"
                  size="12"
                />
                Sub certificate
              </label>
              <div class="rp-fcontrol">
                <selectSubCertificateReq
                  :select-subcertificate="true"
                  :sub-certificate-options="subCertificateOptions"
                  :data-load="dataLoad"
                  @getSubCertificateIdGroup="getSubCertificateIdGroup"
                />
              </div>
            </div>
          </b-col>
        </b-row>

        <b-row class="align-items-start mb-1">
          <b-col
            cols="12"
            md="6"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel">
                <feather-icon
                  icon="CalendarIcon"
                  size="12"
                />
                Request date range
              </label>
              <selectDateReq
                :show-date-input="true"
                :data-load="dataLoad"
              />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel rp-flabel--rose">
                <feather-icon
                  icon="XCircleIcon"
                  size="12"
                />
                Expired date range
              </label>
              <SelectDateExpired
                :show-date-input="true"
                :data-load="dataLoad"
              />
            </div>
          </b-col>
        </b-row>

        <b-row class="align-items-start">
          <b-col
            cols="12"
            md="6"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel rp-flabel--cyan">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="12"
                />
                Issued date range
              </label>
              <SelectDateIssued
                :show-date-input="true"
                :data-load="dataLoad"
              />
            </div>
          </b-col>
          <b-col
            v-if="userRole !== 'office-manager'"
            cols="12"
            md="3"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel rp-flabel--amber">
                <feather-icon
                  icon="HomeIcon"
                  size="12"
                />
                Office
              </label>
              <SelectOffice :show-select-office="true" />
            </div>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <div class="rp-fgroup">
              <label class="rp-flabel">
                <feather-icon
                  icon="TruckIcon"
                  size="12"
                />
                Source
              </label>
              <SelectSorce :data-load="dataLoad" />
            </div>
          </b-col>
        </b-row>

        <!-- Reviewers: single grouped row for all 5 reviewer filters -->
        <div class="rp-reviewers-block">
          <div class="rp-reviewers-head">
            <feather-icon
              icon="UsersIcon"
              size="14"
              class="rp-reviewers-head-icon"
            />
            <span class="rp-reviewers-head-title">Reviewers</span>
            <span class="rp-reviewers-head-sub">1st · 2nd · Inspector · Decision Maker · Marketer</span>
          </div>
          <b-row class="rp-reviewers-row">
            <b-col
              cols="12"
              sm="6"
              lg
              class="rp-reviewers-col"
            >
              <div class="rp-fgroup rp-fgroup--compact">
                <label class="rp-flabel rp-flabel--violet rp-flabel--small">1st Reviewer</label>
                <SelectReviewerFilter
                  query-key="first_reviewer"
                  label="1st Reviewer"
                  :data-load="dataLoad"
                />
              </div>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg
              class="rp-reviewers-col"
            >
              <div class="rp-fgroup rp-fgroup--compact">
                <label class="rp-flabel rp-flabel--violet rp-flabel--small">2nd Reviewer</label>
                <SelectReviewerFilter
                  query-key="second_reviewer"
                  label="2nd Reviewer"
                  :data-load="dataLoad"
                />
              </div>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg
              class="rp-reviewers-col"
            >
              <div class="rp-fgroup rp-fgroup--compact">
                <label class="rp-flabel rp-flabel--violet rp-flabel--small">Inspector</label>
                <SelectReviewerFilter
                  query-key="inspector"
                  label="Inspector"
                  :data-load="dataLoad"
                />
              </div>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg
              class="rp-reviewers-col"
            >
              <div class="rp-fgroup rp-fgroup--compact">
                <label class="rp-flabel rp-flabel--violet rp-flabel--small">Decision Maker</label>
                <SelectReviewerFilter
                  query-key="application_reviewer"
                  label="Decision Maker"
                  :data-load="dataLoad"
                />
              </div>
            </b-col>
            <b-col
              cols="12"
              sm="6"
              lg
              class="rp-reviewers-col"
            >
              <div class="rp-fgroup rp-fgroup--compact">
                <label class="rp-flabel rp-flabel--violet rp-flabel--small">Marketer</label>
                <SelectReviewerFilter
                  query-key="markter"
                  label="Marketer"
                  :data-load="dataLoad"
                />
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-collapse>

    <!-- ================================================================
         TABLE CARD
    ================================================================ -->
    <div class="rp-table-card">
      <!-- Table header bar -->
      <div class="rp-table-topbar">
        <div class="rp-topbar-left">
          <feather-icon
            icon="ListIcon"
            size="16"
            class="mr-75 rp-topbar-icon"
          />
          <span class="rp-topbar-title">Requests list</span>
          <span
            v-if="totalRows"
            class="rp-topbar-count ml-75"
          >{{ totalRows }} records</span>
        </div>
        <div class="d-flex align-items-center rp-topbar-right">
          <b-spinner
            v-if="paginationLoading"
            small
            class="rp-spinner-sm mr-75"
          />
          <span
            v-if="totalRows && !paginationLoading"
            class="rp-topbar-page mr-75"
          >
            <feather-icon
              icon="ChevronsRightIcon"
              size="12"
              class="mr-25"
            />
            Page {{ page }}
          </span>

          <!-- Column visibility toggle -->
          <div class="rp-columns">
            <button
              type="button"
              class="rp-columns-btn"
              @click="showColumnsPanel = !showColumnsPanel"
            >
              <feather-icon
                icon="ColumnsIcon"
                size="13"
                class="mr-25"
              />
              <span>Columns</span>
            </button>

            <!-- Backdrop on mobile when panel is open -->
            <div
              v-if="showColumnsPanel"
              class="rp-columns-backdrop"
              aria-hidden="true"
              @click="showColumnsPanel = false"
            />

            <transition name="fade">
              <div
                v-if="showColumnsPanel"
                class="rp-columns-panel"
              >
                <div class="rp-columns-header">
                  <span class="rp-columns-title">Visible columns</span>
                  <span class="rp-columns-sub">Personalize your view</span>
                  <div class="rp-columns-actions">
                    <button
                      type="button"
                      class="rp-columns-action-link"
                      @click="selectAllColumns"
                    >
                      Select all
                    </button>
                    <span class="rp-columns-action-sep">·</span>
                    <button
                      type="button"
                      class="rp-columns-action-link"
                      @click="deselectAllColumns"
                    >
                      Deselect all
                    </button>
                  </div>
                </div>

                <div class="rp-columns-body">
                  <div
                    v-for="col in columns"
                    :key="col.key || col.field || col.label"
                    class="rp-columns-item"
                  >
                    <label class="rp-columns-pill">
                      <input
                        type="checkbox"
                        class="rp-columns-checkbox"
                        :value="getColumnKey(col)"
                        :checked="visibleColumnKeys.includes(getColumnKey(col))"
                        :disabled="isColumnLocked(col) || isLastVisible(col)"
                        @change="toggleColumn(col)"
                      >
                      <span class="rp-columns-pill-label">
                        {{ col.label || col.text || getColumnKey(col) }}
                      </span>
                    </label>
                  </div>
                </div>

                <div class="rp-columns-footer">
                  <span class="rp-columns-hint">
                    Changes are saved to this browser.
                  </span>
                  <button
                    type="button"
                    class="rp-columns-close"
                    @click="showColumnsPanel = false"
                  >
                    Done
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div
        v-if="dataLoad"
        class="rp-loading"
      >
        <div class="rp-loading-ring" />
        <p class="rp-loading-text">
          Fetching requests…
        </p>
      </div>

      <!-- Table -->
      <div
        v-else
        class="rp-table-scroll-x"
      >
        <b-table
          ref="requestsTable"
          :items="rows"
          :fields="visibleColumns"
          :per-page="perPage"
          primary-key="id"
          hover
          show-empty
          class="rp-table mb-0"
        >
          <template #cell(facility_name)="data">
            <FacilityNameCell
              :data="data"
              :get-small-name="getSmallName"
              :get-span="getSpan"
            />
          </template>
          <template #cell(payment_status)="data">
            <PaymentStatusCell
              :data="data"
              :role="role"
              :get-span="getSpan"
            />
          </template>
          <template #cell(request_code)="data">
            <RequestCodeCell :data="data" />
          </template>
          <template #cell(office_name)="data">
            <OfficeCell
              :data="data"
              :get-small-name="getSmallName"
              :get-span="getSpan"
            />
          </template>
          <template #cell(certificate_name_en)="data">
            <CertificateCell
              :data="data"
              :get-small-name="getSmallName"
              :get-span="getSpan"
            />
          </template>
          <template #cell(sub_certificate_name)="data">
            <SubCertificateCell
              :data="data"
              :role="role"
              :get-small-name="getSmallName"
              :get-span="getSpan"
            />
          </template>
          <template #cell(categoryRequestNo)="data">
            <MakeSmallTextCell :data="data.item.categoryRequestNo" />
          </template>
          <template #cell(client_name)="data">
            <MakeSmallTextCell :data="data.item.client_name" />
          </template>
          <template #cell(first_reviewer_name)="data">
            <ReviewerCell
              :data="data"
              :reviewer-type="data.item.first_reviewer_name"
              :get-small-name="getSmallName"
              :get-span="getSpan"
              :types="['first_reviewer', 'first reviewer']"
            />
          </template>
          <template #cell(first_reviewer_allocation_date)="data">
            <AllocationDate
              :data="data.item.first_reviewer_allocation_date"
            />
          </template>
          <template #cell(second_reviewer_name)="data">
            <ReviewerCell
              :data="data"
              :reviewer-type="data.item.second_reviewer_name"
              :get-small-name="getSmallName"
              :get-span="getSpan"
              :types="['second_reviewer', 'second reviewer']"
            />
          </template>
          <template #cell(second_reviewer_allocation_date)="data">
            <AllocationDate
              :data="data.item.second_reviewer_allocation_date"
            />
          </template>
          <template #cell(inspector)="data">
            <ReviewerCell
              :data="data"
              :reviewer-type="data.item.inspector"
              :get-small-name="getSmallName"
              :get-span="getSpan"
              :types="['inspector', 'inspector']"
            />
          </template>
          <template #cell(inspector_allocation_date)="data">
            <AllocationDate
              :data="data.item.inspector_allocation_date"
            />
          </template>
          <template #cell(application_reviewer)="data">
            <ReviewerCell
              :data="data"
              :reviewer-type="data.item.application_reviewer"
              :get-small-name="getSmallName"
              :get-span="getSpan"
              :types="['application_reviewer', 'Application Reviewer']"
            />
          </template>
          <template #cell(application_reviewer_allocation_date)="data">
            <AllocationDate
              :data="data.item.application_reviewer_allocation_date"
            />
          </template>
          <template #cell(markter)="data">
            <ReviewerCell
              :data="data"
              :reviewer-type="data.item.markter"
              :get-small-name="getSmallName"
              :get-span="getSpan"
              :types="['markter', 'Markter']"
            />
          </template>
          <template #cell(markter_allocation_date)="data">
            <AllocationDate
              :data="data.item.markter_allocation_date"
            />
          </template>

          <template #cell(source_date)="data">
            <AllocationDate
              :data="data.item.source_date"
            />
          </template>

          <template #cell(last_status_update)="data">
            <AllocationDate
              :data="data.item.last_status_update"
            />
          </template>
          <template #cell(due_date)="data">
            <AllocationDate
              :data="data.item.due_date"
            />
          </template>
          <template #cell(expired_at)="data">
            <AllocationDate
              :data="data.item.expired_at"
            />
          </template>
          <template #cell(issued_at)="data">
            <AllocationDate
              :data="data.item.issued_at"
            />
          </template>
          <template #cell(actions)="data">
            <div class="rp-actions">
              <PostEntryAction
                v-if="postContent"
                :data="data"
                :get-all-data="getAllData"
                :api-url="apiUrl"
              />
              <PostContentAction
                v-if="postContent"
                :data="data"
              />
              <ViewContentAction
                v-if="viewContent"
                :data="data"
                :viw-component="viwComponent"
              />
              <EditContentAction
                v-if="editContent"
                :edit-component="editComponent"
                :data="data"
                :type="type"
              />
              <DeleteContentAction
                v-if="deleteContent"
                :data="data"
                :api-url="apiUrl"
                :get-all-data="getAllData"
              />
              <CancelContentAction
                v-if="cancelContent"
                :data="data"
                :api-url="apiUrl"
                :get-all-data="getAllData"
              />
            </div>
          </template>
        </b-table>
      </div>

      <!-- Footer / pagination -->
      <div class="rp-footer">
        <div class="rp-footer-info">
          <feather-icon
            icon="InfoIcon"
            size="13"
            class="mr-50"
          />
          <span v-if="totalRows !== null">{{ totalRows }} total records · {{ perPage }} per page</span>
        </div>
        <Pagination
          :page="page"
          :per-page="perPage"
          :total-rows="totalRows || 0"
          :has-results="false"
          @get-all-data="getAllData"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import buildParams from '@/components/general-table/specific-utils/buildParams'

// Filters
import SelectAndSearch from '@/components/general-table/filter-by/fixed-filter/SelectAndSearch.vue'
import selectDateReq from '@/components/general-table/filter-by/fixed-filter/SelectDateReq.vue'
import SelectOffice from '@/components/general-table/filter-by/fixed-filter/SelectOffice.vue'
import SelectSorce from '@/components/general-table/filter-by/fixed-filter/SelectSorce.vue'
import selectCertificateReq from '@/components/general-table/filter-by/fixed-filter/SelectCertificate.vue'
import selectSubCertificateReq from '@/components/general-table/filter-by/fixed-filter/SelectSubCertificate.vue'
import selectInternalStatuses from '@/components/general-table/filter-by/fixed-filter/SelectInternalStatuses.vue'
import selectExternalStatuses from '@/components/general-table/filter-by/fixed-filter/SelectExternalStatuses.vue'
import SelectReviewerFilter from '@/components/general-table/filter-by/fixed-filter/SelectReviewerFilter.vue'
import SelectDateExpired from '@/components/general-table/filter-by/fixed-filter/SelectDateExpired.vue'
import SelectDateIssued from '@/components/general-table/filter-by/fixed-filter/SelectDateIssued.vue'

// Actions / helpers
import MakeExeclRequest from '@/components/general-table/specific-action/MakeExeclRequest.vue'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'
import PostEntryAction from '@/components/general-table/actions/PostEntryAction.vue'
import PostContentAction from '@/components/general-table/actions/PostContentAction.vue'
import ViewContentAction from '@/components/general-table/actions/ViewContentAction.vue'
import EditContentAction from '@/components/general-table/actions/EditContentAction.vue'
import DeleteContentAction from '@/components/general-table/actions/DeleteContentAction.vue'
import CancelContentAction from '@/components/general-table/actions/CancelContentAction.vue'
import FindRequest from '@/components/general-table/actions/FindRequest.vue'
import FilterType from '@/components/general-table/filter-by/fixed-filter/SelectFilterType.vue'

// Cells and columns (shared with RequestWrapper)
import columns from '../columns-request'
import ReviewerCell from '../template-cell-requests-table/ReviewerCell.vue'
import CertificateCell from '../template-cell-requests-table/CertificateCell.vue'
import SubCertificateCell from '../template-cell-requests-table/SubCertificateCell.vue'
import FacilityNameCell from '../template-cell-requests-table/FacilityNameCell.vue'
import MakeSmallTextCell from '../template-cell-requests-table/MakeSmallTextCell.vue'
import PaymentStatusCell from '../template-cell-requests-table/PaymentStatusCell.vue'
import OfficeCell from '../template-cell-requests-table/OfficeCell.vue'
import RequestCodeCell from '../template-cell-requests-table/RequestCodeCell.vue'
import AllocationDate from '../template-cell-requests-table/AllocationDate.vue'

export default {
  components: {
    SelectAndSearch,
    selectDateReq,
    SelectOffice,
    SelectSorce,
    selectCertificateReq,
    selectSubCertificateReq,
    selectInternalStatuses,
    selectExternalStatuses,
    SelectReviewerFilter,
    SelectDateExpired,
    SelectDateIssued,
    MakeExeclRequest,
    Pagination,
    PostEntryAction,
    PostContentAction,
    ViewContentAction,
    EditContentAction,
    DeleteContentAction,
    CancelContentAction,
    FindRequest,
    FilterType,
    ReviewerCell,
    CertificateCell,
    SubCertificateCell,
    FacilityNameCell,
    MakeSmallTextCell,
    PaymentStatusCell,
    OfficeCell,
    RequestCodeCell,
    AllocationDate,
  },
  props: {
    apiUrlProps: {
      type: String,
      default: () => '',
    },
    haveFindRequest: {
      type: Boolean,
      default: () => false,
    },
    title: {
      type: String,
      default: () => 'Requests',
    },
  },
  data() {
    return {
      advancedOpen: false,
      type: 'page',
      perPage: 20,
      page: 1,
      rows: [],
      totalRows: null,
      dataLoad: false,
      paginationLoading: false,
      skipPage: 0,
      filter: {},
      certificate: [],
      subCertificateOptions: [],
      externalStatuses: [],
      internalStatuses: [],
      paramExel: {},
      userRole: localStorage.getItem('accessRole'),
      addType: '',
      addComponentName: 'add-request',
      editComponent: '',
      viwComponent: '',
      viewContent: false,
      editContent: true,
      postContent: false,
      deleteContent: true,
      cancelContent: false,
      columns,
      visibleColumnKeys: [],
      showColumnsPanel: false,
      role: localStorage.getItem('accessRole'),
      lastScrollY: 0,
      filtersKey: 0,
    }
  },
  computed: {
    apiUrl() {
      const base = `${this.apiUrlProps}?`
      if (!Object.values(this.filter).length) {
        return base.slice(0, -1)
      }
      const queryParams = Object.entries(this.filter)
        .filter(([key, value]) => key && value)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
      return `${base}${queryParams}`
    },
    visibleColumns() {
      // If user has no saved selection yet, show all columns
      if (!this.visibleColumnKeys.length) {
        return this.columns
      }
      const keys = this.visibleColumnKeys
      return this.columns.filter(col => keys.includes(this.getColumnKey(col)))
    },
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        window.scrollTo(0, this.lastScrollY || 0)
      })
    },
  },
  mounted() {
    this.lastScrollY = window.pageYOffset || 0
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('keydown', this.handleGlobalEnterSearch)

    axios
      .get('certificate_group?all=true')
      .then(res => {
        const dataCertificate = decryptData(res.data.payload)?.data?.certificates
        this.certificate = dataCertificate.map(item => ({
          value: item.id,
          text: `${item.name?.ar} - ${item.name?.en}`,
          'data-certificate': item.name?.en,
        }))
      })
    this.initVisibleColumns()
    this.getAllData(this.page)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('keydown', this.handleGlobalEnterSearch)
  },
  methods: {
    handleScroll() {
      this.lastScrollY = window.pageYOffset || 0
    },

    handleGlobalEnterSearch(e) {
      if (e?.key !== 'Enter') return
      if (e?.shiftKey || e?.altKey || e?.ctrlKey || e?.metaKey) return

      // Keep textarea behavior (new lines) if any exist on this page
      const tag = e?.target?.tagName
      if (tag === 'TEXTAREA') return

      e.preventDefault()
      this.getAllData(1)
    },

    getColumnKey(col) {
      return col.key || col.field || col.label || ''
    },

    isColumnLocked(col) {
      const key = this.getColumnKey(col)
      // Keep key technical columns always visible
      return ['request_code', 'actions'].includes(key)
    },

    isLastVisible(col) {
      const key = this.getColumnKey(col)
      const currentlyVisible = this.columns
        .map(c => this.getColumnKey(c))
        .filter(k => this.visibleColumnKeys.includes(k))
      return currentlyVisible.length <= 1 && currentlyVisible.includes(key)
    },

    toggleColumn(col) {
      const key = this.getColumnKey(col)
      if (!key || this.isColumnLocked(col)) return

      const currentlyVisible = new Set(this.visibleColumnKeys)

      if (currentlyVisible.has(key)) {
        // Prevent hiding the last remaining column
        if (currentlyVisible.size <= 1) return
        currentlyVisible.delete(key)
      } else {
        currentlyVisible.add(key)
      }

      this.visibleColumnKeys = Array.from(currentlyVisible)
      localStorage.setItem('requests_visible_columns', JSON.stringify(this.visibleColumnKeys))
    },

    selectAllColumns() {
      const allKeys = this.columns.map(col => this.getColumnKey(col)).filter(Boolean)
      this.visibleColumnKeys = allKeys
      localStorage.setItem('requests_visible_columns', JSON.stringify(this.visibleColumnKeys))
    },

    deselectAllColumns() {
      const lockedKeys = this.columns
        .filter(col => this.isColumnLocked(col))
        .map(col => this.getColumnKey(col))
        .filter(Boolean)
      this.visibleColumnKeys = lockedKeys.length ? lockedKeys : [this.getColumnKey(this.columns[0])]
      localStorage.setItem('requests_visible_columns', JSON.stringify(this.visibleColumnKeys))
    },

    initVisibleColumns() {
      const allKeys = this.columns.map(col => this.getColumnKey(col)).filter(Boolean)
      const stored = localStorage.getItem('requests_visible_columns')

      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed) && parsed.length) {
            const valid = parsed.filter(k => allKeys.includes(k))
            if (valid.length) {
              this.visibleColumnKeys = valid
              return
            }
          }
        } catch (e) {
          // ignore and fall back
        }
      }

      // Default: show all columns
      this.visibleColumnKeys = allKeys
      localStorage.setItem('requests_visible_columns', JSON.stringify(this.visibleColumnKeys))
    },
    async getAllData(page = 1) {
      this.dataLoad = true
      this.paginationLoading = true
      try {
        const params = buildParams(this, page)
        this.skipPage = params.skip
        this.paramExel = params
        const response = await axios.get(this.apiUrl, { params })
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          // console.log(responseData)
          this.rows = responseData?.data[Object.keys(responseData?.data)[0]] || []
          this.externalStatuses = responseData?.data?.external_statuses || []
          this.internalStatuses = responseData?.data?.internal_statuses || []
          this.totalRows = responseData.data.count || responseData.data.length || 0
          this.page = page
        }
      } catch (error) {
        // handled by axios interceptor
      } finally {
        this.dataLoad = false
        this.paginationLoading = false
      }
    },

    goToPageOrOpenModal() {
      if (this.type === 'page') {
        this.$router.push({ name: this.addComponentName })
      } else if (this.$parent && this.$parent.$refs && this.$parent.$refs.genmodal) {
        this.$parent.$refs.genmodal.show()
      }
    },

    handleCertificateId(id) {
      this.$router.push({ query: { ...this.$route.query, certificate: id } })
    },
    getSubCertificateIdGroup(groupIds) {
      this.handleSubCertificateId(groupIds)
    },
    handleSubCertificateId(id) {
      this.$router.push({ query: { ...this.$route.query, sub_certificate: id } })
    },
    handleSubCertificateIdOptions(items) {
      this.subCertificateOptions = items
    },

    escapePopoverText(str) {
      if (str == null || typeof str !== 'string') return ''
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    getSpan(name, action) {
      const esc = this.escapePopoverText
      const safeName = esc(name)
      const safeAction = esc(action || '')

      const iconCert = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>'
      const iconEdit = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>'
      const iconChange = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>'
      const iconAdd = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'

      if (action === 'certificate' || action === 'edit sub certificate') {
        const icon = action === 'certificate' ? iconCert : iconEdit
        const label = action === 'certificate' ? 'Certificate' : 'Sub certificate'
        return `<div class="rp-detail-popover rp-detail-popover--info"><div class="rp-detail-popover__head"><span class="rp-detail-popover__icon">${icon}</span><span class="rp-detail-popover__label">${label}</span></div><div class="rp-detail-popover__body">${safeName || '—'}</div></div>`
      }
      if (name) {
        return `<div class="rp-detail-popover rp-detail-popover--change"><div class="rp-detail-popover__head"><span class="rp-detail-popover__icon">${iconChange}</span><span class="rp-detail-popover__label">${safeAction || 'detail'}</span></div><div class="rp-detail-popover__body">${safeName}</div></div>`
      }
      return `<div class="rp-detail-popover rp-detail-popover--add"><div class="rp-detail-popover__head"><span class="rp-detail-popover__icon">${iconAdd}</span><span class="rp-detail-popover__label">${safeAction || 'detail'}</span></div><div class="rp-detail-popover__body">Click to assign</div></div>`
    },
    getSmallName(input) {
      if (!input) return ''
      const dateMatch = input.match(/\d{4}-\d{2}-\d{2}/)
      if (dateMatch) return dateMatch[0]
      const words = input.trim().split(/\s+/)
      if (words.length <= 2) return input
      return `${words.slice(0, 2).join(' ')}...`
    },
    resetFilters() {
      // Clear all query-based filters, keep only essential routing params (like page if needed)
      this.$router.replace({
        path: this.$route.path,
        query: {},
      })
      this.filtersKey += 1
      this.getAllData(1)
    },
  },
}
</script>

<style src="./RequestsTableRedesigned.scss" scoped lang="scss"></style>
<style src="./RequestsTableRedesigned-popover.scss" lang="scss"></style>
